import {
  ComboBox,
  IComboBox,
  IComboBoxOption,
  IRenderFunction,
  ISelectableDroppableTextProps,
  SearchBox,
  SelectableOptionMenuItemType,
  TextField
} from '@fluentui/react';
import {FC, useCallback, useMemo, useState} from 'react';
import styles from './CustomComboBox.module.scss';
import { ModalWithHeader } from '../ModalWithHeader';
import { TheButton } from '../TheButton';

interface ICustomComboBoxProps {
  defaultOptions: IComboBoxOption[];
  defaultSelectedKey: string | null;
  defaultSelectedText?: string;
  label: string;
  errorMessage?: string;
  addUrlOption?: boolean;
  blockFreeForm?: boolean;
  setTitleValue?: (title: string) => void;
  setUrlValue?: (url: string) => void;
  onChanged: (option: IComboBoxOption) => void;
  onInputValueChange?: (value: string) => void;
}

export const CustomComboBox: FC<ICustomComboBoxProps> = ({
  defaultOptions,
  defaultSelectedKey,
  defaultSelectedText,
  addUrlOption,
  errorMessage,
  blockFreeForm,
  setTitleValue,
  setUrlValue,
  onInputValueChange,
  label,
  onChanged
}) => {
  const initialOptions: IComboBoxOption[] = useMemo(() => {
    const options = [
      {key: 'Header1', text: 'Initial Options', itemType: SelectableOptionMenuItemType.Header},
      ...defaultOptions,
      {key: 'divider2', text: '-', itemType: SelectableOptionMenuItemType.Divider},
      {key: 'Header3', text: 'Free text', itemType: SelectableOptionMenuItemType.Header}
    ];
    if (defaultSelectedKey && !options.find(option => option.key === defaultSelectedKey)) {
      options.push({
        key: defaultSelectedKey as string,
        text: defaultSelectedText ? defaultSelectedText as string : defaultSelectedKey as string,
        data: true
      });
    }
    return options;
  }, [defaultSelectedKey, defaultSelectedText, defaultOptions]);
  const [selectedKey, setSelectedKey] = useState<string | number | null>(defaultSelectedKey);
  const [options, setOptions] = useState(initialOptions);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [titleError, setTitleError] = useState(false);
  const [titleErrorMessage, setTitleErrorMessage] = useState<string | undefined>(undefined);
  const [urlError, setUrlError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const onChange = useCallback(
    (
      event: React.FormEvent<IComboBox>,
      option?: IComboBoxOption,
      index?: number,
      value?: string
    ): void => {
      let key = option?.key || '';
      if (!option && value) {
        key = `${value}`;
        const newOption: IComboBoxOption = {key, text: value, data: true};
        setOptions(prevOptions => [...prevOptions, newOption]);
        onChanged(newOption);
      } else if (option) {
        onChanged(option);
      }
      setSelectedKey(key);
    },
    [onChanged]
  );

  const [query, setQuery] = useState('');
  const onSearchChange = useCallback(
    (event?: React.ChangeEvent<HTMLInputElement> | undefined, newValue?: string | undefined) => {
      setQuery(newValue || '');
    },
    []
  );

  const isOptionDuplicate = useCallback((newText: string | undefined): boolean => {
    if (!newText) return false;
    return options.some(option => option.text.toLocaleLowerCase() === newText.toLocaleLowerCase());
  }, [options]);

  const onRenderList: IRenderFunction<ISelectableDroppableTextProps<IComboBox, IComboBox>> =
    useCallback(
      (props, defaultRender) =>
        defaultRender && props ? (
          <>
            <SearchBox
              placeholder="Filter options"
              onChange={onSearchChange}
              className={styles.search}
            />
            {defaultRender({
              ...props,
              options: (props.options as IComboBoxOption[]).filter(
                item =>
                  !query ||
                  item.itemType === SelectableOptionMenuItemType.Header ||
                  !item.text ||
                  item.text?.indexOf(query) !== -1
              )
            })}
          </>
        ) : null,
      [onSearchChange, query]
    );

    const onClose = useCallback(() => {
      setTitleError(false);
      setUrlError(false);
      setTitle(undefined);
      setUrl(undefined);
      setIsOpen(false);
    },[]);

    function isValidUrl(url: string): boolean {
  const regex = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\-./?%&=]*)?$/;
  return regex.test(url);
}

    const addLink = useCallback(() => {
      setTitleError(false);
      setUrlError(false);
      setTitleErrorMessage(undefined);
       if(!title)
       {
        setTitleError(true);
        setTitleErrorMessage('Title is required');
       }

       if(isOptionDuplicate(title))
       {
        setTitleError(true);
        setTitleErrorMessage('This title already exists');
       }

       if(!isValidUrl(url || ''))
       {
        setUrlError(true);
       }
       if(!title || !url || !isValidUrl(url || '') || isOptionDuplicate(title))
       {
        return;
       }
        const option: IComboBoxOption = {key: url, text: title, data: true};
        setOptions(prevOptions => [...prevOptions, option]);
        setUrl(undefined);
        setTitle(undefined);
        setTitleError(false);
        setTitleErrorMessage(undefined);
        setUrlError(false);
        setSelectedKey(url);
        setTitleValue && setTitleValue(title);
        setUrlValue && setUrlValue(url);
        onChanged(option);
        setIsOpen(false);
    },[title, url, onChanged, setTitleValue, setUrlValue, isOptionDuplicate]);

    const onTitleChange = useCallback(
    (e: any, value: string | undefined) => setTitle(value || ''),
    []
  );
  const onUrlChange = useCallback((e: any, value: string | undefined) => setUrl(value || ''), []);

  return (
    <>
    <div className={styles.customComboBoxContainer}>

      <ComboBox
        label={label}
        onInputValueChange={onInputValueChange}
        allowFreeform={!blockFreeForm}
        className={styles.comboBox}
        autoComplete={'on'}
        options={options}
        onChange={onChange}
        selectedKey={selectedKey}
        errorMessage={errorMessage}
        onRenderList={false ? onRenderList : undefined}
        />
      {addUrlOption ?
      (
      <TheButton iconProps={{iconName: 'AddLink'}} onClick={() => setIsOpen(true)}/>) : undefined}
      </div>
      <ModalWithHeader
        isVisible={isOpen}
        dismiss={onClose}
        header={`Add new link`}>
        <TextField
          label="Title"
          onChange={onTitleChange}
          defaultValue={title}
          required
          errorMessage={titleError ? titleErrorMessage : undefined}
        />
        <TextField
          label="Url"
          onChange={onUrlChange}
          defaultValue={url}
          required
          errorMessage={urlError ? 'Correct url is required' : undefined}
        />
      <div className={styles.customComboboxButtonContainer}>

        <TheButton onClick={onClose} className={styles.button}>
          Cancel
        </TheButton>
        <TheButton onClick={addLink} className={styles.button} primary>
          {'Add'}
        </TheButton>
      </div>
      </ModalWithHeader>
    </>
  );
};
